import { createTheme, ThemeOptions } from '@mui/material/styles';


const colors = {
  primary: {
    main: '#667eea',
    light: '#8b9ef5',
    dark: '#764ba2',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: '#ffffff',
  },
  success: {
    main: '#27ae60',
    light: '#2ecc71',
    dark: '#1e8449',
    contrastText: '#ffffff',
  },
  error: {
    main: '#e74c3c',
    light: '#ff6b5b',
    dark: '#c0392b',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ffc107',
    light: '#ffcd38',
    dark: '#e65100',
    contrastText: '#000000',
  },
  info: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  },
  background: {
    default: '#f5f6fa',
    paper: '#ffffff',
  },
  text: {
    primary: '#2d3436',
    secondary: '#636e72',
    disabled: '#9e9e9e',
  },
  divider: '#e0e0e0',
};


const commonThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      color: colors.text.secondary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: '0.75rem',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
            boxShadow: `0 4px 12px rgba(102,126,234,0.35)`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
          '&:hover': {
            boxShadow: `0 4px 12px rgba(255,152,0,0.35)`,
          },
        },
        outlined: {
          borderColor: colors.divider,
          '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: '#b2bec3',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${colors.divider}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '14px 16px',
          borderBottom: '1px solid #f0f0f0',
        },
        title: {
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#1a1a2e',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '14px 16px',
          '&:last-child': {
            paddingBottom: '14px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: '0.625rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          height: 24,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableHead-root': {
            backgroundColor: '#f8f9fa',
          },
          '& .MuiTableCell-head': {
            fontSize: '0.625rem',
            fontWeight: 600,
            color: '#636e72',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            padding: '12px 14px',
            borderBottom: `1px solid ${colors.divider}`,
          },
          '& .MuiTableCell-body': {
            fontSize: '0.8125rem',
            color: '#2d3436',
            padding: '12px 14px',
            borderBottom: '1px solid #f0f0f0',
          },
          '& .MuiTableRow-root:hover .MuiTableCell-body': {
            backgroundColor: '#fafafa',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#b2bec3',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#f8f9fa',
          border: '1px solid #e8e8e8',
          borderRadius: '8px !important',
          marginBottom: 10,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '0 0 10px 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '12px 14px',
          minHeight: 'unset',
          '&.Mui-expanded': {
            minHeight: 'unset',
          },
        },
        content: {
          margin: 0,
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '0 14px 14px 14px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 3,
          backgroundColor: colors.divider,
        },
        bar: {
          borderRadius: 3,
          background: `linear-gradient(90deg, ${colors.primary.main}, ${colors.primary.dark})`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
        standardWarning: {
          backgroundColor: '#fff3cd',
          borderLeft: '4px solid #ffc107',
          '& .MuiAlert-icon': {
            color: '#e65100',
          },
        },
      },
    },
  },
};

export const lightTheme = createTheme({
  ...commonThemeOptions,
  palette: {
    mode: 'light',
    ...colors,
  },
});


export const darkTheme = createTheme({
  ...commonThemeOptions,
  palette: {
    mode: 'dark',
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
    background: {
      default: '#1a1a2e',
      paper: '#2d2d44',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
      disabled: '#6c6c6c',
    },
    divider: '#3d3d5c',
  },
});

export default lightTheme;
